import React, { useId } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../lib/theme";

export function GaugeRing({ value = 0, label, sublabel, tone = "cyan", size = 190, testid }) {
  const uid = useId().replace(/:/g, "");
  const { viz } = useTheme();
  const [c1, c2] = viz[tone] || viz.cyan;
  const r = size / 2 - 22;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Number(value) || 0));
  const arc = (circ * 0.75 * clamped) / 100;

  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`g-${uid}`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor={c2} />
            <stop offset="100%" stopColor={c1} />
          </linearGradient>
          <linearGradient id={`bevel-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={viz.bevelHi} />
            <stop offset="48%" stopColor={viz.bevelMid} />
            <stop offset="100%" stopColor={viz.bevelLo} />
          </linearGradient>
          <radialGradient id={`core-${uid}`} cx="35%" cy="28%" r="80%">
            <stop offset="0%" stopColor={viz.coreHi} />
            <stop offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <filter id={`glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g transform={`rotate(135 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r + 11} fill="none" stroke={`url(#bevel-${uid})`} strokeWidth="7" />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={viz.ringTrack}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={`${circ * 0.75} ${circ}`}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={`url(#g-${uid})`}
            strokeWidth="12"
            strokeLinecap="round"
            filter={`url(#glow-${uid})`}
            initial={{ strokeDasharray: `0 ${circ}` }}
            animate={{ strokeDasharray: `${arc} ${circ}` }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r - 9}
            fill="none"
            stroke={viz.ringInner}
            strokeWidth="3"
            strokeDasharray={`${circ * 0.75} ${circ}`}
          />
        </g>
        <circle cx={size / 2} cy={size / 2} r={r - 14} fill={`url(#core-${uid})`} />

        <text
          x="50%"
          y="49%"
          textAnchor="middle"
          fill={viz.ink}
          fontFamily="Outfit, sans-serif"
          fontWeight="300"
          fontSize={size * 0.245}
        >
          {clamped}
          <tspan fontSize={size * 0.1} fill={viz.inkDim} dx="2">
            %
          </tspan>
        </text>
        {sublabel && (
          <text
            x="50%"
            y="64%"
            textAnchor="middle"
            fill={viz.inkDim}
            fontFamily="'JetBrains Mono', monospace"
            fontSize={size * 0.058}
          >
            {sublabel}
          </text>
        )}
      </svg>
      {label && <p className="mt-1 text-[11px] uppercase tracking-overline text-slate-400">{label}</p>}
    </div>
  );
}
